// Fejlsporing i browseren (docs/DRIFT.md): uventede fejl – window 'error', 'unhandledrejection' og React-fejlgrænsen – sendes
// renset (src/core/fejlspor.ts) til api.rapporterFejl: i drift edge-funktionen "fejl" → fejl_log, i testversionen konsollen
// i denne browser. Højst 10 pr. sidevisning, og samme fejl højst én gang i minuttet. Valgfri Sentry med VITE_SENTRY_DSN.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import type { Api } from './api/api';
import { FEJL_GRAENSE, browserNavn, erStoej, fejlType, rensRapport, rensSide, sentryKald } from '../core/fejlspor';
import { VERSION } from '../core/version';

let api: Api | null = null;
let sendt = 0;
const senest = new Map<string, number>();

function tekstAf(x: unknown): string {
  if (x instanceof Error) return x.name && x.name !== 'Error' ? `${x.name}: ${x.message}` : x.message;
  if (typeof x === 'string') return x;
  try { return JSON.stringify(x) ?? String(x); } catch { return String(x); }
}

/** Send én fejl (renset). Bruges også direkte, fx af fejlgrænsen med komponentstakken. */
export function rapporterFejl(fejl: unknown, ekstra?: string): void {
  if (!api || sendt >= FEJL_GRAENSE.side) return;
  const besked = tekstAf(fejl), stak = [fejl instanceof Error ? fejl.stack : '', ekstra].filter(Boolean).join('\n');
  if (erStoej(besked, stak)) return;
  const r = rensRapport({ kilde: 'browser', besked, stak, side: rensSide(location.hash || location.pathname), version: VERSION, browser: browserNavn(navigator.userAgent) });
  if (!r) return;
  const k = fejlType(r), nu = Date.now();
  if (nu - (senest.get(k) ?? 0) < 60_000) return;
  senest.set(k, nu); sendt++;
  api.rapporterFejl(r).catch(() => { /* fejlsporingen må aldrig selv give fejl */ });
  const dsn = import.meta.env.VITE_SENTRY_DSN, s = dsn ? sentryKald(dsn, r, import.meta.env.MODE) : null;
  if (s) fetch(s.url, { method: 'POST', body: s.body, keepalive: true }).catch(() => { /* valgfri */ });
}

export function startFejlspor(a: Api): void {
  api = a;
  addEventListener('error', (e) => rapporterFejl(e.error ?? e.message));
  addEventListener('unhandledrejection', (e) => rapporterFejl(e.reason));
}

/** Fanger fejl under visning: personen ser en rolig besked med "Prøv igen" i stedet for en hvid side, og fejlen sendes. */
export class FejlGraense extends Component<{ children: ReactNode }, { fejl: boolean }> {
  state = { fejl: false };
  static getDerivedStateFromError() { return { fejl: true }; }
  componentDidCatch(e: Error, info: ErrorInfo) { rapporterFejl(e, info.componentStack ?? undefined); }
  render() {
    if (!this.state.fejl) return this.props.children;
    return (
      <div className="login-form" style={{ minHeight: '100vh' }}>
        <div className="login-boks kort stak" role="alert">
          <h1>Noget gik galt</h1>
          <p className="svag">Siden kunne ikke vises. Fejlen er sendt til Freemi. Prøv igen – dine data er ikke påvirket.</p>
          <div className="raekke"><button className="knap primaer" onClick={() => location.reload()}>Prøv igen</button></div>
        </div>
      </div>
    );
  }
}
